import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Award, Leaf } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs (handles both local uploads and external URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Default images (fallbacks)
const defaultImages = {
  hero: 'https://images.unsplash.com/photo-1707508672806-aad8d82732e4?w=1600',
  story: 'https://images.pexels.com/photos/7671307/pexels-photo-7671307.jpeg?auto=compress&cs=tinysrgb&w=600',
  gallery: [
    'https://images.pexels.com/photos/8947416/pexels-photo-8947416.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/14064288/pexels-photo-14064288.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.unsplash.com/photo-1619021333065-059e44bd1376?w=400',
    'https://images.unsplash.com/photo-1698717429653-ab97ac5d6b5d?w=400',
  ]
};

const AboutPage = () => {
  const { language, siteSettings } = useStore();
  const t = translations[language];

  // Get images from settings or use defaults
  const heroImage = resolveImageUrl(siteSettings?.aboutHeroImage || siteSettings?.about_hero_image) || defaultImages.hero;
  const storyImage = resolveImageUrl(siteSettings?.aboutStoryImage || siteSettings?.about_story_image) || defaultImages.story;
  const galleryImages = (siteSettings?.aboutGalleryImages || siteSettings?.about_gallery_images || defaultImages.gallery).map(
    (url, i) => resolveImageUrl(url) || defaultImages.gallery[i] || defaultImages.gallery[0]
  );

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-5xl font-bold mb-4">{t.about.title}</h1>
            <p className="text-xl text-gray-200">{t.about.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Story */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={storyImage}
                alt="Equestrian Fashion"
                className="rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {language === 'fr' ? 'Notre Passion' : 'Onze Passie'}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">{t.about.story}</p>
              <Link
                to="/shop"
                className="inline-block bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {t.hero.cta}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t.about.values}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t.about.quality}</h3>
              <p className="text-gray-600">{t.about.qualityDesc}</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t.about.passion}</h3>
              <p className="text-gray-600">{t.about.passionDesc}</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Leaf className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t.about.eco}</h3>
              <p className="text-gray-600">{t.about.ecoDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {language === 'fr' ? 'Notre Univers' : 'Onze Wereld'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <img
                key={index}
                src={img}
                alt=""
                className="aspect-square object-cover rounded-lg"
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
